import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  BookOpen, 
  GraduationCap, 
  LayoutDashboard, 
  LogOut, 
  Menu, 
  User, 
  X,
  FileText,
  Clock,
  Music,
  Heart,
  Shield,
  Crown
} from "lucide-react";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinks = [
    { href: "/dashboard", label: t("nav_dashboard"), icon: LayoutDashboard },
    { href: "/fiches", label: t("nav_fiches"), icon: FileText },
    { href: "/hymne", label: t("nav_hymne"), icon: Music },
    { href: "/exam", label: t("nav_exam"), icon: Clock },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="tricolore-stripe"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-blue-900" />
            <span className="font-serif font-bold text-xl text-blue-900">
              CiviquePrep
            </span>
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "bg-blue-50 text-blue-900"
                      : "text-slate-600 hover:text-blue-900 hover:bg-slate-50"
                  }`}
                >
                  <link.icon className="h-4 w-4" />
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* User Menu */}
          <div className="flex items-center gap-2">
            {user && user.role === "admin" && (
              <Link
                to="/admin"
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/admin")
                    ? "bg-blue-900 text-white"
                    : "bg-blue-50 text-blue-900 hover:bg-blue-100 border border-blue-200"
                }`}
                data-testid="admin-nav-link"
              >
                <Shield className="h-4 w-4" />
                Admin
              </Link>
            )}
            {user && !user.premium && (
              <Link
                to="/premium"
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all shadow-sm"
                data-testid="premium-nav-link"
              >
                <Crown className="h-4 w-4" />
                Premium
              </Link>
            )}
            {user && user.premium && (
              <span className="hidden md:flex items-center gap-1 px-2 py-1 text-xs font-semibold text-amber-600 bg-amber-50 rounded-full border border-amber-200">
                <Crown className="h-3 w-3" />
                Premium
              </span>
            )}
            {user ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2"
                      data-testid="user-menu-trigger"
                    >
                      <div className={`h-8 w-8 rounded-full text-white flex items-center justify-center ${user.role === "admin" ? "bg-amber-500" : "bg-blue-900"}`}>
                        {user.role === "admin" ? <Shield className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-slate-700">
                        {user.name}
                      </span>
                      {user.role === "admin" && (
                        <span className="hidden sm:inline text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">
                          Admin
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem className="text-slate-500 text-xs">
                      {user.email}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => navigate("/donation")}
                      className="cursor-pointer"
                      data-testid="donation-menu-link"
                    >
                      <Heart className="h-4 w-4 mr-2 text-red-500" />
                      {t("nav_donate")}
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => navigate("/admin")}
                          className="cursor-pointer"
                          data-testid="admin-menu-link"
                        >
                          <Shield className="h-4 w-4 mr-2 text-blue-900" />
                          {t("nav_admin_panel")}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer"
                      data-testid="logout-button"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t("nav_logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Language Toggle */}
                <button
                  onClick={toggleLang}
                  className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="lang-toggle"
                  title={lang === "fr" ? "Switch to English" : "Passer en français"}
                >
                  {lang === "fr" ? "EN" : "FR"}
                </button>

                {/* Mobile Menu Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-button"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleLang}
                  className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="lang-toggle"
                >
                  {lang === "fr" ? "EN" : "FR"}
                </button>
                <Link to="/auth">
                  <Button 
                    className="bg-blue-900 hover:bg-blue-800"
                    data-testid="login-button"
                  >
                    {t("nav_login")}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && user && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                  isActive(link.href)
                    ? "bg-blue-50 text-blue-900"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <link.icon className="h-5 w-5" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
